import { createStore } from 'vuex';
import axios from 'axios';

export default createStore({
  state: {
    isLoggedIn: false,
    username: '',
    email: '',
    drawer: null,
    deepestColor: '#f8c602', // 深色状态
    lightestColor: '#FF0501', // 浅色状态
    nodeTransparency: 100, // 默认值
    nodeSize: 10, // 添加节点大小状态
    nodeNameType: '1', // 添加节点名称类型状态
    volcanoNodeNameType: '1', // 火山图节点名称类型状态
    nodeType: '1', // 添加节点类型状态
    lineWidth: 1, // 添加边宽度状态，默认值改为1
    lineTransparency: 20, // 添加边透明度状态，默认值改为20
    lineColor: '#515F6E', // 添加边颜色状态
    nodeColor: '#5470c6', // 添加节点颜色状态
    labelSize: 10, // 火山图标签字体大小状态
    networkLabelSize: 10, // 网络图标签字体大小状态
    sessionUuid: '', // 添加会话 UUID 状态
    sessionName: '', // 添加会话名称状态
    sessionStatus: 0, // 添加会话状态，0表示进行中，1表示已完成
  },
  mutations: {
    updateDrawer(state) {
      state.drawer = !state.drawer;
    },
    SET_LOGGED_IN(state, status) {
      state.isLoggedIn = status;
    },
    SET_USERNAME(state, username) {
      state.username = username;
    },
    SET_EMAIL(state, email) {
      state.email = email;
    },
    SET_DEEPEST_COLOR(state, color) {
      state.deepestColor = color;
    },
    SET_LIGHTEST_COLOR(state, color) {
      state.lightestColor = color;
    },
    SET_NODE_TRANSPARENCY(state, value) {
      state.nodeTransparency = value;
    },
    SET_NODE_SIZE(state, size) {
      state.nodeSize = size;
    },
    SET_NODE_NAME_TYPE(state, type) {
      state.nodeNameType = type;
    },
    SET_VOLCANO_NODE_NAME_TYPE(state, type) {
      state.volcanoNodeNameType = type;
    },
    SET_NODE_TYPE(state, type) {
      state.nodeType = type;
    },
    SET_LINE_WIDTH(state, width) {
      state.lineWidth = width;
    },
    SET_LINE_TRANSPARENCY(state, value) {
      state.lineTransparency = value;
    },
    SET_LINE_COLOR(state, color) {
      state.lineColor = color;
    },
    SET_NODE_COLOR(state, color) {
      state.nodeColor = color;
    },
    SET_LABEL_SIZE(state, size) {
      state.labelSize = size;
    },
    SET_NETWORK_LABEL_SIZE(state, size) {
      state.networkLabelSize = size;
    },
    SET_SESSION_UUID(state, uuid) {
      state.sessionUuid = uuid;
    },
    SET_SESSION_NAME(state, name) {
      state.sessionName = name;
    },
    SET_SESSION_STATUS(state, status) {
      state.sessionStatus = status;
    }
  },
  actions: {
    async fetchLoginStatus({ commit, state }) {
      try {
        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          commit('SET_LOGGED_IN', false);
          commit('SET_USERNAME', '');
          return;
        }

        // 如果已经有登录状态，先检查token是否仍然有效
        if (state.isLoggedIn && state.username) {
          // 已经有登录状态，可以跳过验证，除非明确需要重新验证
          return;
        }

        const data = new FormData();
        data.append('access_token', access_token);
        const response = await axios.post('/api/user/GetTokenInfoView/', data);
        if (response.data.username) {
          commit('SET_LOGGED_IN', true);
          commit('SET_USERNAME', response.data.username);
          commit('SET_EMAIL', response.data.email || '');
        } else {
          commit('SET_LOGGED_IN', false);
          commit('SET_USERNAME', '');
          commit('SET_EMAIL', '');
          // 如果token无效，清除本地存储
          localStorage.removeItem('access_token');
        }
      } catch (error) {
        console.error('Error fetching login status:', error.response ? error.response.data : error.message);
        commit('SET_LOGGED_IN', false);
        commit('SET_USERNAME', '');
        commit('SET_EMAIL', '');
        // 如果API调用失败，清除本地存储的token
        localStorage.removeItem('access_token');
      }
    },
    logout({ commit }) {
      commit('SET_LOGGED_IN', false);
      commit('SET_USERNAME', '');
      commit('SET_EMAIL', '');
      localStorage.removeItem('access_token');
    },
    async createSession({ commit, state }, { module_name }) {
      try {
        // 首先检查用户是否已登录
        if (!state.isLoggedIn) {
          throw new Error('用户未登录');
        }

        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          throw new Error('用户未登录');
        }

        const response = await axios.post('/api/session/CreateSession/', {
          access_token: access_token,
          module_name: module_name
        });

        if (response.data.code === "200") {
          commit('SET_SESSION_UUID', response.data.session);
          commit('SET_SESSION_NAME', response.data.session_name);
          return response.data;
        } else {
          throw new Error(response.data.msg || '创建会话失败');
        }
      } catch (error) {
        console.error('创建会话失败:', error);
        throw error;
      }
    },
    async updateSession({ commit, state }, { session_step, status = 0, isExample } = {}) {
      try {
        // 检查用户是否已登录和是否有会话
        if (!state.isLoggedIn) {
          throw new Error('用户未登录');
        }

        if (!state.sessionUuid) {
          throw new Error('没有活动的会话');
        }

        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          throw new Error('用户未登录');
        }

        const requestData = {
          access_token: access_token,
          session: state.sessionUuid,
          session_step: session_step.toString(),
          status: status.toString()
        };
        if (typeof isExample !== 'undefined') {
          requestData.isExample = Boolean(isExample);
        }

        console.log('发送会话更新请求:', requestData);

        const response = await axios.post('/api/session/UpdateSession/', requestData);

        if (response.data.code === "200") {
          console.log('会话更新成功:', response.data);
          // 更新store中的会话状态
          commit('SET_SESSION_STATUS', status);
          return response.data;
        } else {
          throw new Error(response.data.msg || '更新会话失败');
        }
      } catch (error) {
        console.error('更新会话失败:', error);
        throw error;
      }
    },
  },
  getters: {
    isLoggedIn: (state) => state.isLoggedIn,
    username: (state) => state.username,
    email: (state) => state.email,
    deepestColor: (state) => state.deepestColor, // 正确的 getter
    lightestColor: (state) => state.lightestColor, // 正确的 getter
    sessionUuid: (state) => state.sessionUuid,
    sessionName: (state) => state.sessionName,
    sessionStatus: (state) => state.sessionStatus,
  },
});
